import { Box, Button, Typography } from '@mui/material';
import classNames from 'classnames';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncContent } from '~/components/AsyncContent';
import { NullableTooltip } from '~/components/NullableTooltip';
import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { AbsenceStatus } from '~/data/absences';
import { EditAbsenceDialog } from '~/features/AbsenceChanges/EditAbsenceDialog';
import { RemainingBalance } from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/components/RemainingBalance';
import { AvailableBalances } from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/useVacationBalances';

import { useAddAbsentToday } from './useAddAbsentToday';
import { useSxStyles } from './useStyles';

type Props = {
  buttonText?: string;
  secondButtonText?: string;
  addOtherAbsencesButtonText: string;
  availableBalances: AvailableBalances;
};

export const AddAbsentToday: React.FC<Props> = ({
  buttonText,
  secondButtonText,
  addOtherAbsencesButtonText,
  availableBalances,
}) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    loading,
    failedToLoad,
    todaysAbsence,
    add1DayButtonProps,
    add2DayButtonProps,
    isTabDisabled,
    sickLeavesNotConfigured,
    employeeId,
    addOtherAbsenceDialogState,
  } = useAddAbsentToday();

  const showInstantAbsenceButtons = !(
    todaysAbsence &&
    add1DayButtonProps.disabled &&
    add2DayButtonProps.disabled
  );

  return (
    <AsyncContent
      isLoading={loading}
      failedToLoad={failedToLoad}
      spinnerSize={20}
      customSxStyles={{
        root: sxStyles.root,
        spinner: sxStyles.spinner,
      }}
    >
      {todaysAbsence && (
        <Box
          sx={sxStyles.todaysAbsenceContainer}
          data-cy="todays-absence-container"
        >
          <Typography variant="body2" sx={sxStyles.todaysAbsenceLabel}>
            <TooltippedTruncatedText>
              {t<string>('todaysAbsenceIs')}
            </TooltippedTruncatedText>
          </Typography>

          <Box
            className={classNames(
              todaysAbsence.status === AbsenceStatus.APPROVED && 'approved',
              todaysAbsence.status === AbsenceStatus.PROPOSED && 'proposed',
            )}
            sx={sxStyles.todaysAbsenceStatus}
            data-cy="todays-absence-status"
          >
            {t(todaysAbsence.status.toLowerCase())}
          </Box>
        </Box>
      )}
      {showInstantAbsenceButtons && (
        <Box
          sx={sxStyles.buttonContainer}
          data-cy="add-absent-today-button-container"
        >
          <NullableTooltip
            title={
              sickLeavesNotConfigured ? t<string>('disabledFunctionality') : ''
            }
          >
            <Box sx={sxStyles.tooltipContainer}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                fullWidth={true}
                {...add1DayButtonProps}
              >
                <Typography
                  component="strong"
                  variant="body2"
                  lineHeight={1}
                  fontSize={12}
                >
                  {buttonText ? buttonText : t('addAbsentToday')}
                </Typography>
              </Button>
              {secondButtonText && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  fullWidth={true}
                  sx={sxStyles.secondButton}
                  {...add2DayButtonProps}
                >
                  <Typography
                    component="strong"
                    variant="body2"
                    lineHeight={1}
                    fontSize={12}
                  >
                    {secondButtonText}
                  </Typography>
                </Button>
              )}
            </Box>
          </NullableTooltip>
        </Box>
      )}
      <RemainingBalance availableBalances={availableBalances} />
      <Box
        sx={{ ...sxStyles.buttonContainer, ...sxStyles.otherAbsencesButton }}
        data-cy="add-other-absences-button-container"
      >
        <NullableTooltip
          title={isTabDisabled ? t<string>('disabledFunctionality') : ''}
        >
          <Box sx={sxStyles.tooltipContainer}>
            <Button
              variant="contained"
              color="primary"
              fullWidth={true}
              disabled={isTabDisabled}
              onClick={addOtherAbsenceDialogState.open}
            >
              <Typography
                component="strong"
                variant="body2"
                textAlign="center"
                lineHeight={1}
                fontSize={12}
              >
                {addOtherAbsencesButtonText}
              </Typography>
            </Button>
          </Box>
        </NullableTooltip>
      </Box>

      <EditAbsenceDialog
        employeeId={employeeId}
        onClose={addOtherAbsenceDialogState.close}
        open={addOtherAbsenceDialogState.isOpen}
      />
    </AsyncContent>
  );
};
